/**
 * Partner Service
 *
 * Manages partner/organization CRUD operations and integration management
 * for the multi-tenant SaaS architecture
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  deleteField,
  query,
  where,
  orderBy,
  Timestamp,
  limit as firestoreLimit
} from 'firebase/firestore';
import { db } from '../firebase/config';
import type {
  Partner,
  CreatePartnerInput,
  UpdatePartnerInput,
  PartnerSettings,
  PartnerIntegrations,
  SendGridIntegration,
  MetaIntegration,
  SMSPortalIntegration,
  IntegrationStatus,
  PartnerBranding,
  PartnerContactInfo
} from '../../types/partner.types';
import { PartnerStatus } from '../../types/partner.types';
import {
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
  deleteObject
} from 'firebase/storage';
import { storage } from '../firebase/config';

/**
 * Get partner by ID
 */
export async function getPartner(partnerId: string): Promise<Partner | null> {
  try {
    const partnerRef = doc(db, 'partners', partnerId);
    const partnerSnap = await getDoc(partnerRef);

    if (!partnerSnap.exists()) {
      return null;
    }

    return {
      id: partnerSnap.id,
      ...partnerSnap.data()
    } as Partner;
  } catch (error) {
    console.error('Error getting partner:', error);
    throw new Error(`Failed to get partner: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Get all partners (admin only)
 */
export async function getAllPartners(): Promise<Partner[]> {
  try {
    const partnersRef = collection(db, 'partners');
    const q = query(partnersRef, orderBy('name', 'asc'));
    const snapshot = await getDocs(q);

    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as Partner));
  } catch (error) {
    console.error('Error getting all partners:', error);
    throw new Error(`Failed to get partners: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Get active partners only
 */
export async function getActivePartners(): Promise<Partner[]> {
  try {
    const partnersRef = collection(db, 'partners');
    const q = query(
      partnersRef,
      where('isActive', '==', true),
      where('status', '==', 'active'),
      orderBy('name', 'asc')
    );
    const snapshot = await getDocs(q);

    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as Partner));
  } catch (error) {
    console.error('Error getting active partners:', error);
    throw new Error(`Failed to get active partners: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Create new partner
 */
export async function createPartner(input: CreatePartnerInput): Promise<Partner> {
  try {
    const partnersRef = collection(db, 'partners');
    const newPartnerRef = doc(partnersRef);

    const defaultSettings: PartnerSettings = {
      timezone: 'Africa/Johannesburg',
      language: 'en',
      currency: 'ZAR',
      dateFormat: 'DD/MM/YYYY',
      features: {
        emailCampaigns: true,
        smsCampaigns: true,
        whatsappCampaigns: true,
        facebookLeads: true,
        roundRobin: true
      },
      ...input.settings
    };

    const newPartner: Partner = {
      id: newPartnerRef.id,
      name: input.name,
      displayName: input.displayName,
      isHQ: false, // Default to false, will be true only for PropInvest HQ
      ownerId: input.ownerId,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
      status: PartnerStatus.ONBOARDING,
      isActive: true,
      settings: defaultSettings,
      integrations: {
        sendgrid: {
          isConnected: false,
          isValid: false
        },
        meta: {
          isConnected: false,
          isValid: false
        },
        smsPortal: {
          isConnected: false,
          isValid: false
        }
      }
    };

    await setDoc(newPartnerRef, newPartner);

    return newPartner;
  } catch (error) {
    console.error('Error creating partner:', error);
    throw new Error(`Failed to create partner: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Update partner
 */
export async function updatePartner(partnerId: string, updates: UpdatePartnerInput): Promise<void> {
  try {
    const partnerRef = doc(db, 'partners', partnerId);

    await updateDoc(partnerRef, {
      ...updates,
      updatedAt: Timestamp.now()
    });
  } catch (error) {
    console.error('Error updating partner:', error);
    throw new Error(`Failed to update partner: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Update partner settings
 */
export async function updatePartnerSettings(
  partnerId: string,
  settings: Partial<PartnerSettings>
): Promise<void> {
  try {
    const partnerRef = doc(db, 'partners', partnerId);
    const partnerSnap = await getDoc(partnerRef);

    if (!partnerSnap.exists()) {
      throw new Error('Partner not found');
    }

    const currentPartner = partnerSnap.data() as Partner;

    await updateDoc(partnerRef, {
      settings: {
        ...currentPartner.settings,
        ...settings
      },
      updatedAt: Timestamp.now()
    });
  } catch (error) {
    console.error('Error updating partner settings:', error);
    throw new Error(`Failed to update settings: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Delete partner (soft delete - set inactive)
 */
export async function deletePartner(partnerId: string): Promise<void> {
  try {
    const partnerRef = doc(db, 'partners', partnerId);

    await updateDoc(partnerRef, {
      isActive: false,
      status: PartnerStatus.CANCELLED,
      updatedAt: Timestamp.now()
    });
  } catch (error) {
    console.error('Error deleting partner:', error);
    throw new Error(`Failed to delete partner: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Permanently delete partner (hard delete - use with caution!)
 */
export async function permanentlyDeletePartner(partnerId: string): Promise<void> {
  try {
    const partnerRef = doc(db, 'partners', partnerId);
    await deleteDoc(partnerRef);
  } catch (error) {
    console.error('Error permanently deleting partner:', error);
    throw new Error(`Failed to permanently delete partner: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Update partner integrations
 */
export async function updatePartnerIntegrations(
  partnerId: string,
  integrations: Partial<PartnerIntegrations>
): Promise<void> {
  try {
    const partnerRef = doc(db, 'partners', partnerId);
    const partnerSnap = await getDoc(partnerRef);

    if (!partnerSnap.exists()) {
      throw new Error('Partner not found');
    }

    const currentPartner = partnerSnap.data() as Partner;

    await updateDoc(partnerRef, {
      integrations: {
        ...currentPartner.integrations,
        ...integrations
      },
      updatedAt: Timestamp.now()
    });
  } catch (error) {
    console.error('Error updating partner integrations:', error);
    throw new Error(`Failed to update integrations: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Update SendGrid integration
 */
export async function updateSendGridIntegration(
  partnerId: string,
  sendgridData: Partial<SendGridIntegration>
): Promise<void> {
  try {
    const partnerRef = doc(db, 'partners', partnerId);
    const partnerSnap = await getDoc(partnerRef);

    if (!partnerSnap.exists()) {
      throw new Error('Partner not found');
    }

    const currentPartner = partnerSnap.data() as Partner;

    await updateDoc(partnerRef, {
      integrations: {
        ...currentPartner.integrations,
        sendgrid: {
          ...currentPartner.integrations.sendgrid,
          ...sendgridData,
          lastVerifiedAt: Timestamp.now()
        }
      },
      updatedAt: Timestamp.now()
    });
  } catch (error) {
    console.error('Error updating SendGrid integration:', error);
    throw new Error(`Failed to update SendGrid: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Update Meta/Facebook integration
 */
export async function updateMetaIntegration(
  partnerId: string,
  metaData: Partial<MetaIntegration>
): Promise<void> {
  try {
    const partnerRef = doc(db, 'partners', partnerId);
    const partnerSnap = await getDoc(partnerRef);

    if (!partnerSnap.exists()) {
      throw new Error('Partner not found');
    }

    const currentPartner = partnerSnap.data() as Partner;

    await updateDoc(partnerRef, {
      integrations: {
        ...currentPartner.integrations,
        meta: {
          ...currentPartner.integrations.meta,
          ...metaData,
          lastVerifiedAt: Timestamp.now()
        }
      },
      updatedAt: Timestamp.now()
    });
  } catch (error) {
    console.error('Error updating Meta integration:', error);
    throw new Error(`Failed to update Meta: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Update SMS Portal integration
 */
export async function updateSMSPortalIntegration(
  partnerId: string,
  smsData: Partial<SMSPortalIntegration>
): Promise<void> {
  try {
    const partnerRef = doc(db, 'partners', partnerId);
    const partnerSnap = await getDoc(partnerRef);

    if (!partnerSnap.exists()) {
      throw new Error('Partner not found');
    }

    const currentPartner = partnerSnap.data() as Partner;

    await updateDoc(partnerRef, {
      integrations: {
        ...currentPartner.integrations,
        smsPortal: {
          ...currentPartner.integrations.smsPortal,
          ...smsData,
          lastVerifiedAt: Timestamp.now()
        }
      },
      updatedAt: Timestamp.now()
    });
  } catch (error) {
    console.error('Error updating SMS Portal integration:', error);
    throw new Error(`Failed to update SMS Portal: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Get integration status for a partner
 * Returns user-friendly status info for UI display
 */
export async function getIntegrationStatus(partnerId: string): Promise<{
  sendgrid: IntegrationStatus;
  meta: IntegrationStatus;
  smsPortal: IntegrationStatus;
  whatsapp: IntegrationStatus;
}> {
  try {
    const partner = await getPartner(partnerId);

    if (!partner) {
      throw new Error('Partner not found');
    }

    return {
      sendgrid: {
        name: 'sendgrid',
        displayName: 'SendGrid (Email)',
        isConnected: partner.integrations.sendgrid?.isConnected || false,
        isValid: partner.integrations.sendgrid?.isValid !== false, // Treat undefined as valid (legacy integrations)
        connectedAt: partner.integrations.sendgrid?.connectedAt,
        errorMessage: partner.integrations.sendgrid?.errorMessage,
        canConnect: true, // SendGrid has OAuth
        requiresManualSetup: false
      },
      meta: {
        name: 'meta',
        displayName: 'Facebook/Meta',
        isConnected: partner.integrations.meta?.isConnected || false,
        isValid: partner.integrations.meta?.isValid !== false, // Treat undefined as valid (legacy integrations)
        connectedAt: partner.integrations.meta?.connectedAt,
        errorMessage: partner.integrations.meta?.errorMessage,
        canConnect: true, // Meta has OAuth
        requiresManualSetup: false
      },
      smsPortal: {
        name: 'smsPortal',
        displayName: 'SMS Portal',
        isConnected: partner.integrations.smsPortal?.isConnected || false,
        isValid: partner.integrations.smsPortal?.isValid !== false, // Treat undefined as valid (legacy integrations)
        connectedAt: partner.integrations.smsPortal?.connectedAt,
        errorMessage: partner.integrations.smsPortal?.errorMessage,
        canConnect: false, // No OAuth
        requiresManualSetup: true // Requires assisted setup
      },
      whatsapp: {
        name: 'whatsapp',
        displayName: 'WhatsApp Business',
        // WhatsApp integrates through Meta OAuth - check Meta integration for WhatsApp Business Accounts
        isConnected: !!(partner.integrations.meta?.whatsappBusinessAccounts &&
                       partner.integrations.meta.whatsappBusinessAccounts.length > 0),
        isValid: true, // Always valid if WhatsApp Business Accounts exist
        connectedAt: partner.integrations.meta?.connectedAt,
        errorMessage: partner.integrations.meta?.errorMessage,
        canConnect: false, // No self-service OAuth (goes through Meta OAuth)
        requiresManualSetup: true // Requires Meta OAuth and WhatsApp Business setup
      }
    };
  } catch (error) {
    console.error('Error getting integration status:', error);
    throw new Error(`Failed to get integration status: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Check if partner has required integrations for feature
 */
export async function hasRequiredIntegrations(
  partnerId: string,
  feature: 'email' | 'sms' | 'whatsapp' | 'facebook'
): Promise<boolean> {
  try {
    const partner = await getPartner(partnerId);

    if (!partner) {
      return false;
    }

    // Integration is considered valid if isConnected is true AND isValid is not explicitly false
    // (isValid may be undefined for older/legacy integrations)
    switch (feature) {
      case 'email':
        return !!(partner.integrations.sendgrid?.isConnected && partner.integrations.sendgrid?.isValid !== false);
      case 'sms':
        return !!(partner.integrations.smsPortal?.isConnected && partner.integrations.smsPortal?.isValid !== false);
      case 'whatsapp':
        // WhatsApp integrates through Meta OAuth - check Meta integration for WhatsApp Business Accounts
        return !!(partner.integrations.meta?.whatsappBusinessAccounts &&
                  partner.integrations.meta.whatsappBusinessAccounts.length > 0);
      case 'facebook':
        return !!(partner.integrations.meta?.isConnected && partner.integrations.meta?.isValid !== false);
      default:
        return false;
    }
  } catch (error) {
    console.error('Error checking required integrations:', error);
    return false;
  }
}

/**
 * Upload partner logo to Firebase Storage
 */
export async function uploadPartnerLogo(
  partnerId: string,
  file: File
): Promise<string> {
  try {
    // Validate file type
    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml'];
    if (!validTypes.includes(file.type)) {
      throw new Error('Invalid file type. Only PNG, JPG, and SVG are allowed.');
    }

    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      throw new Error('File size exceeds 5MB limit.');
    }

    // Create storage reference
    const fileExtension = file.name.split('.').pop();
    const logoRef = storageRef(storage, `partners/${partnerId}/logo.${fileExtension}`);

    // Upload file
    await uploadBytes(logoRef, file, {
      contentType: file.type,
      customMetadata: {
        uploadedAt: new Date().toISOString(),
        originalName: file.name
      }
    });

    // Get download URL
    const downloadURL = await getDownloadURL(logoRef);

    // Update partner document with logo URL
    await updatePartnerBranding(partnerId, { logoUrl: downloadURL });

    return downloadURL;
  } catch (error) {
    console.error('Error uploading partner logo:', error);
    throw new Error(`Failed to upload logo: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Delete partner logo from Firebase Storage
 */
export async function deletePartnerLogo(partnerId: string): Promise<void> {
  try {
    const partner = await getPartner(partnerId);

    if (!partner?.branding?.logoUrl) {
      return; // No logo to delete
    }

    // Try to delete all common formats
    const formats = ['png', 'jpg', 'jpeg', 'svg'];
    for (const format of formats) {
      try {
        const logoRef = storageRef(storage, `partners/${partnerId}/logo.${format}`);
        await deleteObject(logoRef);
      } catch (error) {
        // File might not exist, continue
      }
    }

    // Update partner document to remove logo URL
    const partnerRef = doc(db, 'partners', partnerId);
    await updateDoc(partnerRef, {
      'branding.logoUrl': deleteField(),
      updatedAt: Timestamp.now()
    });
  } catch (error) {
    console.error('Error deleting partner logo:', error);
    throw new Error(`Failed to delete logo: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Update partner branding
 */
export async function updatePartnerBranding(
  partnerId: string,
  branding: Partial<PartnerBranding>
): Promise<void> {
  try {
    const partnerRef = doc(db, 'partners', partnerId);
    const partnerSnap = await getDoc(partnerRef);

    if (!partnerSnap.exists()) {
      throw new Error('Partner not found');
    }

    const currentPartner = partnerSnap.data() as Partner;

    await updateDoc(partnerRef, {
      branding: {
        ...currentPartner.branding,
        ...branding
      },
      updatedAt: Timestamp.now()
    });
  } catch (error) {
    console.error('Error updating partner branding:', error);
    throw new Error(`Failed to update branding: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Update partner contact information
 */
export async function updatePartnerContactInfo(
  partnerId: string,
  contactInfo: Partial<PartnerContactInfo>
): Promise<void> {
  try {
    const partnerRef = doc(db, 'partners', partnerId);
    const partnerSnap = await getDoc(partnerRef);

    if (!partnerSnap.exists()) {
      throw new Error('Partner not found');
    }

    const currentPartner = partnerSnap.data() as Partner;

    await updateDoc(partnerRef, {
      contactInfo: {
        ...currentPartner.contactInfo,
        ...contactInfo
      },
      updatedAt: Timestamp.now()
    });
  } catch (error) {
    console.error('Error updating partner contact info:', error);
    throw new Error(`Failed to update contact info: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}
